import { useCallback, useEffect, useRef, useState } from 'react';
import { Download, RefreshCw, ShieldCheck } from 'lucide-react';
import { usePlatformAuth as useAuth } from '@/contexts/platformSession';
import { dashboardRequest, withinDeadline } from '@/lib/platformDashboard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/lib/supabase';
import { exactCostCsv, formatExactMoney, record, validMonth, validateCostReport, type CostReport } from '../../supabase/functions/_shared/platform-cost-contract.ts';
import './platform-usage.css';

const currentMonth = () => new Date().toISOString().slice(0, 7);
const dateLabel = (value: string | null) => value ? new Date(value).toLocaleString(undefined, { timeZone: 'UTC', dateStyle: 'medium', timeStyle: 'short' }) + ' UTC' : 'Not supplied';


export function PlatformUsagePage() {
  const { user, sessionVersion, loading } = useAuth();
  return <main className="cost-page ph-no-capture sentry-mask" data-ph-mask data-sentry-mask>
    <header className="cost-header"><div><p className="cost-eyebrow">Rize OS · Hella private</p><h1>Platform costs</h1><p>Provider-confirmed software costs and source evidence.</p></div><ShieldCheck aria-hidden="true" size={28} /></header>
    {loading ? <p role="status">Checking your session…</p> : user ? <AuthorizedUsage key={`${user.id}:${sessionVersion}`} /> : <HellaSignIn />}
  </main>;
}

function HellaSignIn() {
  const { signIn, sessionError } = useAuth();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  return <section className="cost-panel cost-login"><h2>Hella sign-in</h2><p>Access requires your verified Hella account and an explicit platform access grant.</p>
    <form onSubmit={async event => {
      event.preventDefault();
      const values = new FormData(event.currentTarget);
      setBusy(true); setError('');
      try { await signIn(String(values.get('email')), String(values.get('password'))); }
      catch { setError('Sign-in failed. Check your credentials or account access.'); }
      finally { setBusy(false); }
    }}>
      <label htmlFor="platform-email">Email<Input id="platform-email" className="cost-field" name="email" type="email" autoComplete="username" required /></label>
      <label htmlFor="platform-password">Password<Input id="platform-password" className="cost-field" name="password" type="password" autoComplete="current-password" required /></label>
      <Button className="cost-button" type="submit" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</Button>
      {error && <p className="cost-error" role="alert">{error}</p>}
      {sessionError && <p className="cost-error" role="alert">{sessionError}</p>}
    </form>
    <a href="/login">Rize OS sign-in</a>
  </section>;
}

function AuthorizedUsage() {
  const { user, signOut } = useAuth();
  const [month, setMonth] = useState(currentMonth);
  const [report, setReport] = useState<CostReport | null>(null);
  const [permission, setPermission] = useState<'checking' | 'allowed' | 'denied'>('checking');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [factor, setFactor] = useState<string | null>(null);
  const generation = useRef(0);
  const operation = useRef<AbortController | null>(null);
  const cancelPending = useCallback(() => { generation.current++; operation.current?.abort(); }, []);

  const verifyAndLoad = useCallback(async () => {
    operation.current?.abort();
    const controller = new AbortController(); operation.current = controller;
    const turn = ++generation.current;
    setPermission('checking'); setReport(null); setError(''); setNotice(''); setBusy(false);
    try {
      const assurance = await withinDeadline(supabase.auth.mfa.getAuthenticatorAssuranceLevel(), controller.signal);
      if (turn !== generation.current) return;
      if (assurance.error) throw new Error('MFA verification unavailable.');
      if (assurance.data.nextLevel === 'aal2' && assurance.data.currentLevel !== 'aal2') {
        const factors = await withinDeadline(supabase.auth.mfa.listFactors(), controller.signal);
        if (turn !== generation.current) return;
        if (factors.error) throw new Error('MFA verification unavailable.');
        const enrolled = factors.data.totp.find(value => value.status === 'verified');
        setFactor(enrolled?.id ?? null);
        throw new Error(enrolled ? 'Complete your authentication challenge.' : 'Complete MFA for your account before opening costs.');
      }
      setFactor(null);
      const access = await dashboardRequest({ action: 'access' }, controller.signal);
      if (turn !== generation.current) return;
      if (!record(access) || access.allowed !== true) throw new Error('Your account does not have platform cost access.');
      setPermission('allowed');
      const value = await dashboardRequest({ action: 'report', month }, controller.signal);
      if (turn !== generation.current) return;
      if (!validateCostReport(value, month)) throw new Error('The canonical report could not be validated.');
      setReport(value);
    } catch (failure) {
      if (turn !== generation.current) return;
      setReport(null); setPermission('denied');
      setError(failure instanceof Error ? failure.message : 'Access verification unavailable.');
    }
  }, [month]);

  useEffect(() => {
    const initial = window.setTimeout(() => void verifyAndLoad(), 0);
    const refresh = () => { if (!document.hidden) void verifyAndLoad(); };
    const interval = window.setInterval(refresh, 30_000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => { window.clearTimeout(initial); window.clearInterval(interval); window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); cancelPending(); };
  }, [verifyAndLoad, cancelPending]);

  const action = async (kind: 'refresh' | 'export') => {
    const turn = ++generation.current;
    operation.current?.abort();
    const controller = new AbortController(); operation.current = controller;
    setBusy(true); setError(''); setNotice(''); setPermission('checking'); setReport(null);
    try {
      // The report action independently rechecks current Auth/MFA and central access.
      const value = await dashboardRequest({ action: 'report', month }, controller.signal);
      if (turn !== generation.current) return;
      if (!validateCostReport(value, month)) throw new Error('The canonical report could not be validated.');
      if (kind === 'export') {
        const url = URL.createObjectURL(new Blob([exactCostCsv(value)], { type: 'text/csv;charset=utf-8' }));
        const link = document.createElement('a'); link.href = url; link.download = `rize-os-platform-costs-${month}.csv`;
        link.click(); window.setTimeout(() => URL.revokeObjectURL(url), 1000);
        setNotice('Exact source CSV downloaded.');
      } else {
        const refreshed = await dashboardRequest({ action: 'refresh', month }, controller.signal);
        if (turn !== generation.current) return;
        if (!record(refreshed) || refreshed.queued !== true || refreshed.month !== month || typeof refreshed.request_id !== 'string') throw new Error('Refresh acknowledgment unavailable.');
        setNotice(`Collection queued. Request ${refreshed.request_id}. Existing evidence remains visible until collection finishes.`);
      }
      setReport(value); setPermission('allowed');
    } catch (failure) {
      if (turn !== generation.current) return;
      setReport(null); setPermission('denied'); setError(failure instanceof Error ? failure.message : 'Access verification unavailable.');
    } finally { if (turn === generation.current) setBusy(false); }
  };

  return <>
    <div className="cost-toolbar"><span className="cost-identity">{user?.email}</span><Button className="cost-button cost-secondary" onClick={() => { generation.current++; operation.current?.abort(); setReport(null); setPermission('denied'); void signOut(); }}>Sign out</Button></div>
    <section className="cost-panel cost-controls" aria-label="Reporting controls">
      <label htmlFor="platform-month">UTC reporting month<Input id="platform-month" className="cost-field" type="month" value={month} min="2026-09" max={currentMonth()} onChange={event => { if (validMonth(event.target.value)) { generation.current++; operation.current?.abort(); setReport(null); setPermission('checking'); setMonth(event.target.value); } }} /></label>
      <Button className="cost-button" disabled={permission !== 'allowed' || busy || !report} onClick={() => void action('refresh')}><RefreshCw size={16} aria-hidden="true" />Refresh collection</Button>
      <Button className="cost-button cost-secondary" disabled={permission !== 'allowed' || busy || !report} onClick={() => void action('export')}><Download size={16} aria-hidden="true" />Download exact CSV</Button>
    </section>
    {permission === 'checking' && <p role="status">{busy ? 'Checking access and processing…' : 'Checking access and loading confirmed evidence…'}</p>}
    {error && <section className="cost-panel"><p className="cost-error" role="alert">{error}</p><Button className="cost-button cost-secondary" onClick={() => void verifyAndLoad()}>Retry access</Button></section>}
    {factor && <form className="cost-panel cost-login" onSubmit={async event => {
      event.preventDefault(); const values = new FormData(event.currentTarget); setBusy(true); setError('');
      const turn = ++generation.current;
      try {
        const result = await withinDeadline(supabase.auth.mfa.challengeAndVerify({ factorId: factor, code: String(values.get('code')) }));
        if (turn !== generation.current) return;
        if (result.error) setError('Authentication code was not accepted.'); else void verifyAndLoad();
      } catch { if (turn === generation.current) setError('Authentication verification unavailable. Retry to continue.'); }
      finally { if (turn === generation.current) setBusy(false); }
    }}><label htmlFor="platform-code">Authenticator code<Input id="platform-code" className="cost-field" name="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" required /></label><Button className="cost-button" disabled={busy}>Verify</Button></form>}
    {notice && <p role="status">{notice}</p>}
    {permission === 'allowed' && report && <CostEvidence report={report} />}
  </>;
}

function CostEvidence({ report }: { report: CostReport }) {
  const confirmed = report.lines.filter(line => line.amount_usd !== null);
  return <div className="cost-evidence">
    <section className="cost-panel"><p className="cost-eyebrow">Provider-confirmed subtotal · USD · {report.month}</p><h2 className="cost-total">{confirmed.length ? formatExactMoney(report.summary.confirmed_usd) : 'Unconfirmed'}</h2><p>{report.summary.complete ? 'All configured sources have confirmed evidence.' : 'Partial coverage. This subtotal is not the complete platform bill.'}</p><p>{confirmed.length} of {report.lines.length} sources have confirmed amounts. Unconfirmed sources: {report.summary.unknown_sources}.</p><p className="cost-muted">Report assembled {dateLabel(report.generated_at)}. Billing observation dates and exclusive confirmed-through cutoffs appear with each source.</p>{report.collection && <p>Collection: {report.collection.state}{report.collection.completed_at ? ` · Attempt completed ${dateLabel(report.collection.completed_at)}` : ''}{report.collection.error ? ` · ${report.collection.error}` : ''}</p>}</section>
    {report.warnings.length > 0 && <section className="cost-panel" aria-label="Coverage warnings"><h2>Coverage warnings</h2><ul>{report.warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul></section>}
    {report.lines.length === 0 ? <section className="cost-panel"><h2>Provider coverage is not configured.</h2><p>No complete bill or zero charge can be established from this report.</p></section> : <section className="cost-source-grid" aria-label="Provider evidence">{report.lines.map(line => <article className="cost-panel cost-source" key={line.source_key}>
      <div className="cost-source-title"><h2>{line.vendor}</h2><span className="cost-badge">{line.status === 'ok' ? 'Confirmed' : line.status}</span></div><p>{line.component} · {line.environment}</p>
      <p className="cost-amount">{line.amount_usd === null ? 'Unconfirmed' : formatExactMoney(line.amount_usd)}</p>
      <dl><dt>Source</dt><dd>{line.source_key}</dd><dt>Evidence basis</dt><dd>{line.basis === 'vendor_usage' ? 'Provider billing response' : line.basis === 'invoice' ? 'Actual invoice' : 'Awaiting evidence'}</dd><dt>Observed</dt><dd>{dateLabel(line.observed_at)}</dd><dt>Confirmed through (exclusive)</dt><dd>{line.coverage_through ? dateLabel(line.coverage_through) : line.basis === 'invoice' ? 'Invoice evidence; cutoff not supplied' : 'Not confirmed'}</dd><dt>Evidence reference</dt><dd>{line.evidence_ref ?? 'Not supplied'}</dd><dt>Account reference</dt><dd>{line.account_ref}</dd></dl>{line.detail && <p>{line.detail}</p>}
    </article>)}</section>}
  </div>;
}
